import { useState } from 'react';
import { Badge, Button, Input } from '../../components/ui';
import {
  dueBadgeLabel,
  isFollowupOverdue,
  nextActionErrorMessage,
  useCreateNextAction,
  useNextActionFor,
} from './nextAction';
import type { NextActionRecordKind } from './nextAction';
import { todayIso } from './trackerFormat';

/**
 * "What do I do next?" — the second thing in the Lead drawer, and the same
 * box on the Prospect drawer.
 *
 * Kyu's definition: the nearest open task on this record. See nextAction.ts
 * for why the read is scoped to one record and why creation goes through
 * `create_sales_followup` rather than a direct insert.
 */
export function NextActionBlock({
  recordId,
  recordKind,
  onError,
}: {
  recordId: string;
  recordKind: NextActionRecordKind;
  onError: (error: unknown) => void;
}) {
  const actionQuery = useNextActionFor(recordKind, recordId);
  const createAction = useCreateNextAction();
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [dueOn, setDueOn] = useState('');

  // Pending, not empty. "Nothing to do next" is a claim, and making it before
  // the task has arrived would show it for a moment on every drawer opened.
  if (actionQuery.isPending) {
    return <p className="tracker-field-note">Checking for open tasks…</p>;
  }

  const action = actionQuery.data ?? null;

  const submit = () => {
    if (title.trim() === '') {
      return;
    }
    createAction.mutate(
      {
        title: title.trim(),
        due: dueOn === '' ? null : dueOn,
        kind: recordKind,
        recordId,
      },
      {
        onSuccess: () => {
          setTitle('');
          setDueOn('');
          setAdding(false);
        },
        onError: (cause) => onError(new Error(nextActionErrorMessage(cause.message))),
      },
    );
  };

  if (action !== null && !adding) {
    return (
      <div className="lead-next-action">
        <p className="lead-next-action-title">{action.title}</p>
        <Badge tone={isFollowupOverdue(action.due_date, todayIso()) ? 'danger' : 'neutral'}>
          {dueBadgeLabel(action.due_date, todayIso())}
        </Badge>
      </div>
    );
  }

  if (!adding) {
    return (
      <div className="lead-next-action lead-next-action--empty">
        {/* Kyu: "If there is no open task, I do not want the drawer to feel
            like it has no answer to 'what do I do next?'" So the empty state
            is the fastest path to the thing the section is for, rather than a
            sentence saying there is nothing.

            Laid out like the Follow-up Round box directly beneath it — the
            sentence, then a small button under it — because Kyu circled the
            old version (sentence and full-size button side by side on one
            line) as the odd one out between two boxes that stack. */}
        <p className="tracker-field-note">No open task on this {recordKind}.</p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setAdding(true);
          }}
        >
          Add a task
        </Button>
      </div>
    );
  }

  return (
    <div className="lead-next-action lead-next-action--adding">
      <Input
        label="Task"
        value={title}
        placeholder="What needs doing?"
        onChange={(event) => {
          setTitle(event.target.value);
        }}
      />
      <Input
        label="Due"
        type="date"
        value={dueOn}
        onChange={(event) => {
          setDueOn(event.target.value);
        }}
      />
      <div className="lead-next-action-actions">
        {/* A task with no title is not a task. Disabled rather than accepted
            and silently dropped, which is the shape of half the bugs found
            this month. */}
        <Button onClick={submit} disabled={title.trim() === '' || createAction.isPending}>
          {createAction.isPending ? 'Saving…' : 'Save task'}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setAdding(false);
            setTitle('');
            setDueOn('');
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
